/**
 * Atlas — Delete account confirmation modal.
 *
 * Two friction points before the destructive button enables:
 *   1. The user must type their own email exactly.
 *   2. The user must enter their current password.
 *
 * Both are sent to the server which re-verifies the password. The email
 * match is enforced UI-side only — it's intent-confirmation, not auth.
 * An optional "Help us improve" reason field is forwarded to the audit
 * log for product feedback.
 *
 * Mounted from SettingsPage; on success the parent calls `onDeleted()`
 * which logs out + redirects.
 */
import React, { useState } from 'react';
import { authAPI } from '../../services/api';

export default function DeleteAccountModal({ open, user, onClose, onDeleted }) {
  const [emailConfirm, setEmailConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  // Type-to-confirm: enables the destructive button only when the typed
  // email matches the account email exactly (case-insensitive trim).
  const intentConfirmed =
    !!user?.email &&
    emailConfirm.trim().toLowerCase() === user.email.toLowerCase();
  const canSubmit = intentConfirmed && password.length > 0 && !submitting;

  async function submit() {
    setError(null);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await authAPI.deleteMyAccount({
        password,
        reason: reason.trim() || undefined,
      });
      // Wipe local form state so the modal can't be reopened with stale
      // credentials sitting in memory.
      setPassword('');
      setEmailConfirm('');
      setReason('');
      onDeleted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend returns 403 on wrong password — surface that distinctly so
      // the user knows to retry the password field, not the whole flow.
      if (err?.response?.status === 403) {
        setError(typeof detail === 'string' ? detail : 'Password is incorrect.');
      } else {
        setError(
          typeof detail === 'string' ? detail :
          err?.message ||
          'Could not delete your account. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
    >
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-head">
          <div>
            <div className="eyebrow" style={{ color: 'var(--pa-negative)' }}>Danger zone</div>
            <h3 className="serif" style={{
              margin: 0, fontSize: 18, color: 'var(--pa-navy)', fontWeight: 600,
            }}>
              Delete your account
            </h3>
          </div>
          <button className="btn ghost sm" onClick={onClose} disabled={submitting} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{
            padding: 12,
            background: 'var(--pa-negative-bg)',
            color: 'var(--pa-negative)',
            borderRadius: 'var(--pa-radius-md)',
            fontSize: 13,
          }}>
            This action is permanent. Your account, properties, EOIs and uploads
            will be erased. Audit logs are retained with your identity scrubbed
            for legal compliance.
          </div>

          <Field label="Type your email to confirm">
            <input
              type="email"
              autoCapitalize="off"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              value={emailConfirm}
              onChange={(e) => setEmailConfirm(e.target.value)}
              placeholder={user?.email || ''}
              className="input"
              disabled={submitting}
            />
            {emailConfirm && !intentConfirmed ? (
              <Helper tone="warn">Doesn't match your account email.</Helper>
            ) : null}
          </Field>

          <Field label="Current password">
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              disabled={submitting}
            />
          </Field>

          <Field label="Help us improve (optional)">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              rows={3}
              className="input"
              style={{ resize: 'vertical' }}
              placeholder="Why are you leaving? We read every response."
              disabled={submitting}
            />
            <Helper>{reason.length} / 500</Helper>
          </Field>

          {error ? (
            <div style={{
              padding: 10, background: 'var(--pa-negative-bg)', color: 'var(--pa-negative)',
              borderRadius: 'var(--pa-radius-md)', fontSize: 13,
            }}>{error}</div>
          ) : null}
        </div>

        <div className="modal-foot" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="btn danger"
            onClick={submit}
            disabled={!canSubmit}
          >
            {submitting ? 'Deleting…' : 'Delete account permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="eyebrow" style={{ color: 'var(--pa-slate-2)' }}>{label}</span>
      {children}
    </label>
  );
}

function Helper({ children, tone }) {
  const color =
    tone === 'warn' ? 'var(--pa-caution)' :
    'var(--pa-slate-2)';
  return (
    <span style={{ fontSize: 11, color, marginTop: 2 }}>{children}</span>
  );
}
